import { computed, ComputedRef, reactive } from "vue"
import axios from "axios"

import useNotify from "@Composables/useNotify"
import type ReplaceableRouter from "@Modules/shared/interfaces/ReplaceableRouter"
import { ParseServerError, ProcessServerErrors } from "@Modules/shared/Api/ServerErrors"

export interface UpdatePasswordParams {
  currentPassword: string
  password: string
  passwordConfirmation: string
}

export interface BackupCodesResult {
  backup_codes: string[]
  created: string
}

export type SecurityApi = ReplaceableRouter<SecurityApi, "get" | "post" | "patch" | "delete"> & {
  loading: ComputedRef<boolean>
  errors: ComputedRef<Record<string, string>>
  backupCodesCreatedDate: ComputedRef<string | null>
  updatePassword: (params: UpdatePasswordParams) => Promise<boolean>
  enableTwoFactor: () => Promise<boolean>
  disableTwoFactor: () => Promise<boolean>
  verifyTwoFactor: (params: { token: string }) => Promise<boolean>
  getQrCodeUrl: () => Promise<string | null>
  generateBackupCodes: () => Promise<BackupCodesResult | null>
  deleteBackupCodes: () => Promise<boolean>
  fetchBackupCodesDate: () => Promise<void>
}

const BASE_URL = "/app/api/settings/security"
const TFA_URL = `${BASE_URL}/two-factor-auth`

export default function SecurityApi(): SecurityApi {
  const state = reactive<{
    loading: boolean
    errors: Record<string, string>
    backupCodesCreatedDate: string | null
  }>({
    loading: false,
    errors: {},
    backupCodesCreatedDate: null,
  })

  let router: Pick<HttpInterface, "get" | "post" | "patch" | "delete"> = axios
  const { notifySuccess, notifyError } = useNotify()

  function clearErrors(): void {
    Object.keys(state.errors).forEach((key) => delete state.errors[key])
  }

  function setErrors(error: unknown): void {
    const { fieldErrors, serverMessage } = ParseServerError(error)
    if (fieldErrors) {
      state.errors = ProcessServerErrors(fieldErrors)
    } else if (serverMessage) {
      notifyError({ text: serverMessage })
    } else {
      notifyError()
    }
  }

  const that: SecurityApi = {
    loading: computed(() => state.loading),
    errors: computed(() => state.errors),
    backupCodesCreatedDate: computed(() => state.backupCodesCreatedDate),

    async updatePassword({ currentPassword, password, passwordConfirmation }: UpdatePasswordParams): Promise<boolean> {
      clearErrors()
      state.loading = true

      try {
        await router.post(`${BASE_URL}/password`, {
          _method: "PUT",
          current_password: currentPassword,
          password,
          password_confirmation: passwordConfirmation,
        })
        notifySuccess("Your password has been updated.")
        return true
      } catch (error) {
        setErrors(error)
        return false
      } finally {
        state.loading = false
      }
    },

    async enableTwoFactor(): Promise<boolean> {
      clearErrors()
      state.loading = true

      try {
        await router.post(TFA_URL, { type: 1 })
        return true
      } catch (error) {
        if (ParseServerError(error).status === 428) {
          return false
        }
        setErrors(error)
        return false
      } finally {
        state.loading = false
      }
    },

    async disableTwoFactor(): Promise<boolean> {
      clearErrors()
      state.loading = true

      try {
        await router.delete(TFA_URL)
        return true
      } catch (error) {
        setErrors(error)
        return false
      } finally {
        state.loading = false
      }
    },

    async verifyTwoFactor({ token }: { token: string }): Promise<boolean> {
      clearErrors()
      state.loading = true

      try {
        await router.post(`${TFA_URL}/verify`, { token })
        return true
      } catch (error) {
        setErrors(error)
        return false
      } finally {
        state.loading = false
      }
    },

    async getQrCodeUrl(): Promise<string | null> {
      try {
        const response = await router.get<{ url?: string }>(TFA_URL)
        return response.data?.url ?? null
      } catch {
        return null
      }
    },

    async generateBackupCodes(): Promise<BackupCodesResult | null> {
      clearErrors()
      state.loading = true

      try {
        const response = await router.post<BackupCodesResult>(`${TFA_URL}/backup-codes`, {})
        const data = response.data
        state.backupCodesCreatedDate = data.created
        return data
      } catch (error) {
        setErrors(error)
        return null
      } finally {
        state.loading = false
      }
    },

    async deleteBackupCodes(): Promise<boolean> {
      clearErrors()
      state.loading = true

      try {
        await router.delete(`${TFA_URL}/backup-codes`)
        state.backupCodesCreatedDate = null
        notifySuccess("Your backup codes have been deleted.")
        return true
      } catch (error) {
        setErrors(error)
        return false
      } finally {
        state.loading = false
      }
    },

    async fetchBackupCodesDate(): Promise<void> {
      try {
        const response = await router.get<{ created?: string | null }>(`${TFA_URL}/backup-codes`)
        state.backupCodesCreatedDate = response.data?.created ?? null
      } catch {
        state.backupCodesCreatedDate = null
      }
    },

    setRouter(newRouter: Pick<HttpInterface, "get" | "post" | "patch" | "delete">): SecurityApi {
      router = newRouter
      return that
    },
  }

  return that
}
